import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Save, X, FileText, Loader2, Check, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import api from '../utils/api';
import { toast } from 'sonner';

/**
 * TwoStepUpload Component
 * 
 * A reusable component that implements the two-step upload process:
 * 1. Stage: File is uploaded to temporary staging
 * 2. Save: Admin clicks "Save" to commit permanently
 * 
 * Props:
 * - onSaveComplete: (fileUrl, fileName) => void - Called when file is saved permanently
 * - onCancel: () => void - Called when upload is cancelled
 * - acceptedTypes: object - File types to accept (default: all)
 * - maxSize: number - Max file size in bytes (default: 50MB)
 */
const TwoStepUpload = ({ 
  onSaveComplete, 
  onCancel,
  acceptedTypes = {},
  maxSize = 50 * 1024 * 1024,
  label = "Upload File"
}) => {
  const [status, setStatus] = useState('idle'); // idle, staging, staged, saving, saved, error
  const [stagedFile, setStagedFile] = useState(null);
  const [error, setError] = useState(null);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Check file size
    if (file.size > maxSize) {
      toast.error(`File too large. Maximum size is ${Math.round(maxSize / 1024 / 1024)}MB`);
      return;
    }

    setStatus('staging');
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/files/stage', formData);
      
      setStagedFile({
        stagedFilename: response.data.staged_filename,
        originalFilename: response.data.original_filename,
        fileSize: response.data.file_size,
      });
      setStatus('staged');
      toast.success('File staged! Click "Save" to store permanently.');
    } catch (err) {
      console.error('Staging error:', err);
      setError(err.response?.data?.detail || 'Failed to stage file');
      setStatus('error');
      toast.error('Failed to stage file');
    }
  }, [maxSize]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: acceptedTypes,
    disabled: status === 'staging' || status === 'saving' || status === 'staged',
  });

  const handleSave = async () => {
    if (!stagedFile) return;

    setStatus('saving');
    setError(null);

    try {
      const response = await api.post(`/files/save?staged_filename=${stagedFile.stagedFilename}`);
      
      setStatus('saved');
      toast.success('File saved permanently!');
      
      if (onSaveComplete) {
        onSaveComplete(response.data.file_url, response.data.filename);
      }
    } catch (err) {
      console.error('Save error:', err);
      setError(err.response?.data?.detail || 'Failed to save file');
      setStatus('error');
      toast.error('Failed to save file');
    }
  };

  const handleCancel = async () => {
    if (stagedFile) {
      try {
        await api.delete(`/files/stage/${stagedFile.stagedFilename}`);
      } catch (err) {
        console.error('Cancel error:', err);
      }
    }
    
    setStagedFile(null);
    setStatus('idle');
    setError(null);
    
    if (onCancel) {
      onCancel();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Render based on status
  if (status === 'saved') {
    return (
      <div className="border-2 border-emerald-500 bg-emerald-500/10 rounded-xl p-6 text-center">
        <Check className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
        <p className="text-emerald-400 font-medium">File Saved Successfully!</p>
        <p className="text-sm text-slate-400 mt-1">{stagedFile?.originalFilename}</p>
      </div>
    );
  }

  if (status === 'staged') {
    return (
      <div className="border-2 border-amber-500 bg-amber-500/10 rounded-xl p-6">
        <div className="flex items-start space-x-4">
          <div className="bg-amber-500/20 p-3 rounded-xl">
            <FileText className="w-8 h-8 text-amber-500" />
          </div>
          <div className="flex-1">
            <p className="text-amber-400 font-medium text-sm uppercase tracking-wider mb-1">File Staged</p>
            <p className="text-white font-semibold">{stagedFile.originalFilename}</p>
            <p className="text-sm text-slate-400">{formatFileSize(stagedFile.fileSize)}</p>
          </div>
        </div>
        
        <div className="mt-4 p-3 bg-amber-500/5 border border-amber-500/30 rounded-lg">
          <p className="text-sm text-amber-300 flex items-center">
            <AlertCircle className="w-4 h-4 mr-2" />
            This file is staged temporarily. Click <strong className="mx-1">Save</strong> to store permanently.
          </p>
        </div>
        
        <div className="flex space-x-3 mt-4">
          <Button 
            onClick={handleSave}
            className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
            data-testid="save-file-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            Save Permanently
          </Button>
          <Button 
            onClick={handleCancel}
            variant="outline"
            className="border-slate-600 hover:border-red-500 hover:text-red-400"
            data-testid="cancel-upload-btn"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="border-2 border-red-500 bg-red-500/10 rounded-xl p-6 text-center">
        <AlertCircle className="w-12 h-12 mx-auto mb-3 text-red-500" />
        <p className="text-red-400 font-medium">Upload Failed</p>
        <p className="text-sm text-slate-400 mt-1">{error}</p>
        <Button 
          onClick={() => { setStatus('idle'); setError(null); }}
          variant="outline"
          className="mt-4"
        >
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div 
      {...getRootProps()} 
      className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
        isDragActive 
          ? 'border-blue-500 bg-blue-500/10' 
          : status === 'staging' 
            ? 'border-slate-500 bg-slate-700/50 cursor-wait' 
            : 'border-slate-600 hover:border-slate-500 bg-slate-700/50'
      }`}
      data-testid="file-dropzone"
    >
      <input {...getInputProps()} data-testid="file-input" />
      
      {status === 'staging' ? (
        <>
          <Loader2 className="w-12 h-12 mx-auto mb-3 text-blue-500 animate-spin" />
          <p className="text-sm text-blue-400">Staging file...</p>
        </>
      ) : (
        <>
          <Upload className="w-12 h-12 mx-auto mb-3 text-slate-400" />
          <p className="text-sm text-slate-300 font-medium">{label}</p>
          <p className="text-xs text-slate-500 mt-1">
            {isDragActive ? 'Drop the file here' : 'Drag & drop or click to select'}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Max size: {Math.round(maxSize / 1024 / 1024)}MB
          </p>
        </>
      )}
    </div>
  );
};

export default TwoStepUpload;
